import { useEffect, useMemo, useState } from 'react';
import { useStore } from '@/store/useStore';
import { Users, Search, Building2, Phone, Mail, UserSquare2, Plus, Pencil, ShieldCheck, Link2, Copy, Loader2, Activity, Download, ListChecks } from 'lucide-react';
import { cn } from '@/utils/cn';
import { initialsOf, hashHue } from '@/utils/colors';
import { getRegistrationLink, getPayrollReport, type PayrollReportRow } from '@/auth/adminClient';
import { downloadTableExcel } from '@/utils/exportTable';
import { useTzShiftMs } from '@/hooks/useTzShift';
import { type ID } from '@/types';
import { Modal } from '../ui/Modal';
import { usePerms } from '@/auth/usePerms';
import {
  computeWorkloadRows, currentMonthKey, monthLabel,
  WORKLOAD_GAMES_HEAD, workloadGameRow, type WorkloadGame,
} from '@/utils/workload';
import { WorkloadGamesModal } from '../workload/WorkloadGames';

type Tab = 'employees' | 'teams' | 'workload';

export function PersonnelPage() {
  const { employees, teams, branches, deals, game_formats, filterBranchId, setModal, setPage, personnelTab, setPersonnelTab, currentUser } = useStore();
  const tzShiftMs = useTzShiftMs();
  const p = usePerms();
  // Кассир заходит сюда ТОЛЬКО ради выгрузки отработанных игр (право pages.workload):
  // список сотрудников с телефонами и бригады ему не нужны. Поэтому вкладки «Сотрудники»
  // и «Бригады» гейтим полным правом «Персонал», а «Загрузку» — любым из двух.
  const canPersonnel = p.pages.personnel;
  const canWorkload = p.pages.personnel || p.pages.workload;
  const onlyWorkload = !canPersonnel && canWorkload;
  // Зарплату в «Загрузке» видят только роли с правом «Ставки» (финансист/руководитель/
  // супер-админ) — кассиру (у него только pages.workload) показываем часы, но не деньги.
  const canSeePayroll = p.pages.pay_rates;
  // Вкладки «Сотрудники»/«Команды» живут в сторе (подменю в шапке на них переключает).
  // «Загрузка» — только локальная (в сторе такого значения нет, стор не трогаем): держим
  // локальную копию tab и синхронизируем её со стором для двух «штатных» вкладок.
  const [tabLocal, setTabLocal] = useState<Tab>(personnelTab);
  useEffect(() => { setTabLocal(personnelTab); }, [personnelTab]);
  // У кого есть только «Загрузка» — раздел всегда открыт на ней (других вкладок нет).
  const tab: Tab = onlyWorkload ? 'workload' : tabLocal;
  const setTab = (t: Tab) => {
    setTabLocal(t);
    if (t === 'employees' || t === 'teams') setPersonnelTab(t);
  };
  const [search, setSearch] = useState('');
  const [month, setMonth] = useState(currentMonthKey);
  const toast = useStore((s) => s.toast);
  const [sharedLink, setSharedLink] = useState<string | null>(null);
  const [sharedBusy, setSharedBusy] = useState(false);
  const openSharedLink = async () => {
    setSharedBusy(true);
    try { setSharedLink(await getRegistrationLink()); }
    catch (e) { toast({ kind: 'error', text: e instanceof Error ? e.message : 'Ошибка' }); }
    finally { setSharedBusy(false); }
  };

  const branchById = useMemo(() => new Map(branches.map((b) => [b.id, b])), [branches]);

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    return employees
      // Уволенных (is_active=false) в списке не показываем — карточка остаётся резолвиться
      // в прошлых играх/зарплатных отчётах, но из «Сотрудники» убрана (чат 02.11.2026).
      .filter((e) => e.is_active !== false)
      .filter((e) => filterBranchId === 'all' || e.branch_id === filterBranchId)
      .filter((e) => !q ||
        e.full_name.toLowerCase().includes(q) ||
        (e.role ?? '').toLowerCase().includes(q) ||
        (branchById.get(e.branch_id ?? '')?.name ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [employees, filterBranchId, search, branchById]);

  // Сколько сделок ведёт каждый сотрудник
  const dealsByEmployee = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of deals) {
      if (d.responsible_id) m.set(d.responsible_id, (m.get(d.responsible_id) ?? 0) + 1);
    }
    return m;
  }, [deals]);

  const filteredTeams = useMemo(() => {
    const q = search.trim().toLowerCase();
    return teams
      .filter((t) => filterBranchId === 'all' || t.branch_id === filterBranchId)
      .filter((t) => !q || t.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teams, filterBranchId, search]);

  // Группировка сотрудников по филиалам
  const employeesByBranch = useMemo(() => {
    const m = new Map<string, typeof employees>();
    for (const e of filteredEmployees) {
      const key = e.branch_id ?? '_none';
      const arr = m.get(key) ?? [];
      arr.push(e);
      m.set(key, arr);
    }
    return m;
  }, [filteredEmployees]);

  // ── Вкладка «Загрузка»: проведённые игры/часы линейного персонала за выбранный месяц ──
  // Сам расчёт — общий с экраном «Мои игры» (src/utils/workload.ts), чтобы ведущий и кассир
  // сверялись по одинаковым цифрам.
  const workloadRows = useMemo(
    () => computeWorkloadRows({ deals, employees, teams, month, branchId: filterBranchId }),
    [deals, employees, teams, filterBranchId, month],
  );

  // Зарплата за месяц — считается сервером по тому же справочнику ставок, что и экран
  // «Ставки» (по формуле филиал × формат × длительность × кол-во × уровень × роль).
  // Один запрос на всех сотрудников месяца/филиала, а не по сотруднику — дешевле и быстрее.
  const [payReport, setPayReport] = useState<PayrollReportRow[] | null>(null);
  const [payLoading, setPayLoading] = useState(false);
  useEffect(() => {
    if (!canSeePayroll || tab !== 'workload') return;
    const [y, m] = month.split('-').map(Number);
    if (!y || !m) return;
    const fromMs = new Date(y, m - 1, 1).getTime();
    const toMs = new Date(y, m, 1).getTime();
    let cancelled = false;
    setPayLoading(true);
    getPayrollReport(fromMs, toMs, filterBranchId as any)
      .then((r) => { if (!cancelled) setPayReport(r); })
      .catch((e) => { if (!cancelled) toast({ kind: 'error', text: `Не удалось посчитать зарплату (${e instanceof Error ? e.message : e})` }); })
      .finally(() => { if (!cancelled) setPayLoading(false); });
    return () => { cancelled = true; };
  }, [canSeePayroll, tab, month, filterBranchId]); // eslint-disable-line

  const payByEmployee = useMemo(() => new Map((payReport ?? []).map((r) => [r.employee_id, r])), [payReport]);

  // Агентские (5% за личную продажу) не зависят от состава смены — сотрудник мог продать
  // игру, которую сам не отрабатывал, и тогда в workloadRows (считается по составу/бригаде)
  // его вообще нет. Доливаем таких отдельными строками (0 игр/часов, только деньги), а тем,
  // кто уже есть, — доливаем недостающие агентские игры в их список (для раскрытия по клику).
  const displayWorkloadRows = useMemo(() => {
    if (!canSeePayroll || !payReport) return workloadRows;
    const result = workloadRows.map((r) => ({ ...r }));
    const byId = new Map(result.map((r) => [r.id, r]));
    for (const rep of payReport) {
      let row = byId.get(rep.employee_id);
      const existingDealIds = new Set((row?.items ?? []).map((it) => it.deal.id));
      const extraItems: WorkloadGame[] = [];
      for (const it of rep.items) {
        if (existingDealIds.has(it.deal_id)) continue;
        const deal = deals.find((d) => d.id === it.deal_id);
        if (deal) extraItems.push({ deal, role: 'agent' });
      }
      if (extraItems.length === 0) continue;
      if (row) {
        row.items = [...row.items, ...extraItems];
      } else {
        row = { id: rep.employee_id, name: rep.full_name, role: 'Агентские', games: 0, hours: 0, items: extraItems };
        result.push(row);
        byId.set(rep.employee_id, row);
      }
    }
    return result;
  }, [canSeePayroll, payReport, workloadRows, deals]);

  const workloadTotals = useMemo(() => ({
    games: workloadRows.reduce((s, r) => s + r.games, 0),
    hours: workloadRows.reduce((s, r) => s + r.hours, 0),
    pay: (payReport ?? []).reduce((s, r) => s + r.total, 0),
  }), [workloadRows, payReport]);

  const exportWorkload = () => {
    const head = canSeePayroll ? ['Сотрудник', 'Роль', 'Игр за месяц', 'Часов', 'Зарплата'] : ['Сотрудник', 'Роль', 'Игр за месяц', 'Часов'];
    const rows = displayWorkloadRows.map((r) => canSeePayroll
      ? [r.name, r.role, r.games, r.hours.toFixed(1), payByEmployee.get(r.id)?.total ?? 0]
      : [r.name, r.role, r.games, r.hours.toFixed(1)]);
    downloadTableExcel(`Загрузка-${month}.xlsx`, head, rows);
    toast({ kind: 'success', text: 'Загрузка выгружена' });
  };

  /** Плоский лист «сотрудник × игра» — тот самый список, по которому сверяются ведущий и кассир. */
  const exportWorkloadGames = () => {
    const rows: unknown[][] = [];
    for (const r of displayWorkloadRows) {
      for (const it of r.items) {
        rows.push(workloadGameRow({
          employeeName: r.name,
          item: it,
          formats: game_formats,
          branchName: branchById.get(it.deal.branch_id ?? '')?.name ?? '',
          tzShiftMs,
        }));
      }
    }
    downloadTableExcel(`Загрузка-игры-${month}.xlsx`, WORKLOAD_GAMES_HEAD, rows);
    toast({ kind: 'success', text: 'Список игр выгружен' });
  };

  // Список игр одного сотрудника за месяц (открывается кликом по строке «Загрузки»).
  const [gamesModal, setGamesModal] = useState<{ id: ID; name: string; role: string; hours: number; items: WorkloadGame[] } | null>(null);
  const openGamesDeal = (id: ID) => {
    const nav = gamesModal?.items.map((it) => it.deal.id);
    setGamesModal(null);
    setModal({ kind: 'deal', id }, nav);
  };

  // Зарплата по игре (для раскрытого списка игр сотрудника) — только с правом «Ставки».
  const gamesModalPay = useMemo(() => {
    if (!canSeePayroll || !gamesModal) return null;
    const rep = payByEmployee.get(gamesModal.id);
    if (!rep) return null;
    const byDeal = new Map<string, number | null>();
    const reasonByDeal = new Map<string, string | undefined>();
    for (const it of rep.items) {
      byDeal.set(it.deal_id, it.ok ? it.total ?? null : null);
      if (!it.ok) reasonByDeal.set(it.deal_id, it.reason);
    }
    return { byDeal, reasonByDeal, total: rep.total };
  }, [canSeePayroll, gamesModal, payByEmployee]);

  const workloadBranchLabel = filterBranchId === 'all'
    ? 'Все филиалы'
    : (branchById.get(filterBranchId)?.name ?? 'Филиал');

  return (
    <div className="h-full flex flex-col">
      {/* Top toolbar */}
      <div className="min-h-14 px-3 sm:px-5 py-2 sm:py-0 flex items-center flex-wrap gap-3 border-b border-line bg-surface-1 flex-shrink-0">
        <div className="flex items-center gap-1.5 bg-surface-2 rounded-full p-1 max-w-full overflow-x-auto">
          {/* Счётчики отражают ВИДИМЫЙ список (с учётом выбранного филиала и поиска),
              а не весь штат/все команды — иначе при фильтре по филиалу цифры расходились. */}
          {canPersonnel && (
            <>
              <TabBtn label="Сотрудники" icon={Users} active={tab === 'employees'} count={filteredEmployees.length}
                onClick={() => setTab('employees')} />
              <TabBtn label="Бригады/Команды" icon={UserSquare2} active={tab === 'teams'} count={filteredTeams.length}
                onClick={() => setTab('teams')} />
            </>
          )}
          {canWorkload && (
            <TabBtn label="Загрузка" icon={Activity} active={tab === 'workload'} count={displayWorkloadRows.length}
              onClick={() => setTab('workload')} />
          )}
        </div>

        <div className="flex-1" />

        {tab === 'workload' ? (
          <>
            <span className="text-xs text-ink-3 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{workloadBranchLabel}</span>
            <input
              type="month"
              className="input h-9 w-[160px]"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              title="Месяц для расчёта загрузки"
            />
            <button className="btn-soft" onClick={exportWorkload} disabled={displayWorkloadRows.length === 0}
              title="Свод: сотрудник, роль, сколько игр и часов за месяц">
              <Download className="w-4 h-4" /> Excel
            </button>
            <button className="btn-soft" onClick={exportWorkloadGames} disabled={displayWorkloadRows.length === 0}
              title="Подробно: каждая игра каждого сотрудника отдельной строкой">
              <ListChecks className="w-4 h-4" /> Excel: список игр
            </button>
          </>
        ) : (
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-3" />
            <input
              className="input pl-8 h-9"
              placeholder={`Поиск ${tab === 'employees' ? 'сотрудника' : 'команды'}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}

        {currentUser?.access_role === 'superadmin' && (
          <button className="btn-soft" onClick={() => setPage('access')} title="Пользователи, роли, приглашения">
            <ShieldCheck className="w-4 h-4" /> Доступы
          </button>
        )}

        {/* Линейный персонал заводится по ссылкам (общая / приглашение). Плюс — ручное
            заведение супер-админом (напр. сотрудник из Битрикса, чья должность не даёт
            авто-доступа): открывает карточку сотрудника с полями Bitrix ID + роль входа. */}
        {tab === 'employees' && currentUser?.access_role === 'superadmin' && (
          <>
            <button className="btn-soft" onClick={() => setModal({ kind: 'employee', id: null })} title="Завести сотрудника вручную (в т.ч. с входом через Битрикс)">
              <Plus className="w-4 h-4" /> Добавить сотрудника
            </button>
            <button className="btn-primary" disabled={sharedBusy} onClick={() => void openSharedLink()} title="Одна ссылка для всех линейных — каждый регистрируется сам">
              {sharedBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Общая ссылка
            </button>
          </>
        )}
        {tab === 'teams' && (
          <button className="btn-primary" onClick={() => setModal({ kind: 'team', id: null })}>
            <Plus className="w-4 h-4" /> Команда
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-5">
        {tab === 'employees' && (
          <div className="max-w-[1280px] mx-auto">
            {filteredEmployees.length === 0 ? (
              <EmptyEmployees onShareLink={currentUser?.access_role === 'superadmin' ? () => void openSharedLink() : undefined} />
            ) : (
              <div className="space-y-5">
                {Array.from(employeesByBranch.entries()).map(([branchId, emps]) => {
                  const branch = branchById.get(branchId);
                  return (
                    <section key={branchId}>
                      <div className="flex items-center gap-2 mb-2 px-1">
                        {branch ? (
                          <>
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: branch.color }} />
                            <h3 className="text-sm font-bold text-ink-1">{branch.name}</h3>
                            <span className="text-xs text-ink-3">· {emps.length} {emps.length === 1 ? 'человек' : emps.length < 5 ? 'человека' : 'человек'}</span>
                          </>
                        ) : (
                          <h3 className="text-sm font-bold text-ink-3">Без филиала</h3>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {emps.map((e) => (
                          <EmployeeCard
                            key={e.id}
                            name={e.full_name}
                            role={e.role}
                            phone={e.phone}
                            email={e.email}
                            levelHost={e.level_host}
                            levelDj={e.level_dj}
                            avatar={e.avatar}
                            dealsCount={dealsByEmployee.get(e.id) ?? 0}
                            onClick={() => setModal({ kind: 'employee', id: e.id })}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'teams' && (
          <div className="max-w-[1280px] mx-auto">
            {filteredTeams.length === 0 ? (
              <div className="panel p-8 text-center">
                <UserSquare2 className="w-10 h-10 text-ink-3 mx-auto mb-2" />
                <p className="text-sm text-ink-2">Команд пока нет</p>
                <button className="btn-primary mt-3" onClick={() => setModal({ kind: 'team', id: null })}>
                  <Plus className="w-4 h-4" /> Создать команду
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredTeams.map((t) => {
                  const branch = t.branch_id ? branchById.get(t.branch_id) : null;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setModal({ kind: 'team', id: t.id })}
                      className="panel p-4 text-left hover:border-accent transition-all group"
                      style={{ borderTopWidth: 3, borderTopColor: t.color }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-base font-bold text-ink-1">{t.name}</h3>
                        <Pencil className="w-3.5 h-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      {branch && (
                        <div className="flex items-center gap-1 text-[11px] text-ink-3 mb-2">
                          <Building2 className="w-3 h-3" /> {branch.name}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1">
                        {t.member_ids.slice(0, 6).map((mid) => {
                          const m = employees.find((x) => x.id === mid);
                          if (!m) return null;
                          return (
                            <div key={mid} className="w-7 h-7 rounded-full grid place-items-center text-[10px] font-bold text-white"
                              style={{ backgroundColor: `hsl(${hashHue(m.full_name)}, 55%, 48%)` }}
                              title={m.full_name}>
                              {initialsOf(m.full_name)}
                            </div>
                          );
                        })}
                        {t.member_ids.length > 6 && (
                          <div className="w-7 h-7 rounded-full bg-surface-2 grid place-items-center text-[10px] font-semibold text-ink-2">
                            +{t.member_ids.length - 6}
                          </div>
                        )}
                        {t.member_ids.length === 0 && (
                          <span className="text-[11px] text-ink-3 italic py-1">пусто</span>
                        )}
                      </div>
                      <div className="mt-2 text-[10px] text-ink-3 flex items-center gap-2">
                        <span>{t.member_ids.length} {t.member_ids.length === 1 ? 'участник' : 'участников'}</span>
                        {t.is_archived && <span className="chip !h-4 !text-[9px]">архив</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {tab === 'workload' && (
          <div className="max-w-[1000px] mx-auto">
            {displayWorkloadRows.length === 0 ? (
              <div className="panel p-8 text-center">
                <Activity className="w-10 h-10 text-ink-3 mx-auto mb-2" />
                <p className="text-sm text-ink-2">За этот месяц проведённых игр нет</p>
              </div>
            ) : (
              <div className="panel p-0 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm table-fixed">
                    <thead>
                      <tr className="text-left text-xs text-ink-3 border-b border-line">
                        <th className="px-2 sm:px-4 py-2.5 font-medium">Сотрудник</th>
                        <th className="px-2 sm:px-4 py-2.5 font-medium w-[84px] sm:w-[120px]">Роль</th>
                        <th className="px-2 sm:px-4 py-2.5 font-medium text-right w-[52px] sm:w-[96px]">Игр</th>
                        <th className="px-2 sm:px-4 py-2.5 font-medium text-right w-[56px] sm:w-[76px]">Часов</th>
                        {canSeePayroll && <th className="px-2 sm:px-4 py-2.5 font-medium text-right w-[90px] sm:w-[120px]">Зарплата</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {displayWorkloadRows.map((r) => {
                        const pay = payByEmployee.get(r.id);
                        return (
                        <tr key={r.id}
                          className="border-b border-line/50 hover:bg-surface-2/40 cursor-pointer"
                          title="Показать список игр за месяц"
                          onClick={() => setGamesModal({ id: r.id, name: r.name, role: r.role, hours: r.hours, items: r.items })}>
                          <td className="px-2 sm:px-4 py-2.5">
                            <span className="flex items-center gap-2.5 min-w-0">
                              <span className="hidden sm:grid w-8 h-8 rounded-full place-items-center text-white font-bold text-[11px] flex-shrink-0"
                                style={{ backgroundColor: `hsl(${hashHue(r.name)}, 55%, 48%)` }}>
                                {initialsOf(r.name)}
                              </span>
                              <span className="font-semibold text-ink-1 truncate">{r.name}</span>
                            </span>
                          </td>
                          <td className="px-2 sm:px-4 py-2.5 text-ink-2">
                            <span className="chip !h-5 !text-[10px] !px-1.5 max-w-full truncate">{r.role}</span>
                          </td>
                          <td className="px-2 sm:px-4 py-2.5 text-right text-ink-1 tabular-nums font-semibold">{r.games}</td>
                          <td className="px-2 sm:px-4 py-2.5 text-right text-ink-1 tabular-nums">{r.hours.toFixed(1)}</td>
                          {canSeePayroll && (
                            <td className="px-2 sm:px-4 py-2.5 text-right text-ink-1 tabular-nums font-semibold">
                              {payLoading && !payReport ? <Loader2 className="w-3.5 h-3.5 animate-spin inline-block text-ink-3" /> : (
                                <>
                                  {pay ? `${pay.total.toLocaleString('ru-RU')} ₸` : '—'}
                                  {pay && pay.unresolved > 0 && (
                                    <span className="text-amber-600 font-normal ml-1" title={`${pay.unresolved} игр не посчиталось — не хватает данных`}>⚠</span>
                                  )}
                                </>
                              )}
                            </td>
                          )}
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-line font-bold text-ink-1 bg-surface-2/50">
                        <td className="px-2 sm:px-4 py-2.5 truncate" colSpan={2}>ИТОГО · {displayWorkloadRows.length} чел.</td>
                        <td className="px-2 sm:px-4 py-2.5 text-right tabular-nums">{workloadTotals.games}</td>
                        <td className="px-2 sm:px-4 py-2.5 text-right tabular-nums">{workloadTotals.hours.toFixed(1)}</td>
                        {canSeePayroll && <td className="px-2 sm:px-4 py-2.5 text-right tabular-nums">{workloadTotals.pay.toLocaleString('ru-RU')} ₸</td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {gamesModal && (
        <WorkloadGamesModal
          name={gamesModal.name}
          role={gamesModal.role}
          hours={gamesModal.hours}
          items={gamesModal.items}
          periodText={monthLabel(month)}
          branchText={workloadBranchLabel}
          formats={game_formats}
          tzShiftMs={tzShiftMs}
          onOpenDeal={openGamesDeal}
          onClose={() => setGamesModal(null)}
          payByDeal={gamesModalPay?.byDeal}
          payReasonByDeal={gamesModalPay?.reasonByDeal}
          payTotal={gamesModalPay?.total}
        />
      )}

      {sharedLink && (
        <Modal open onClose={() => setSharedLink(null)} title="Общая ссылка для регистрации" size="md"
          footer={<button className="btn-primary" onClick={() => setSharedLink(null)}>Готово</button>}>
          <div className="space-y-3">
            <p className="text-sm text-ink-2">Одна ссылка на всех линейных сотрудников. Отправьте её всем — каждый зайдёт, сам выберет должность и филиал, придумает логин и пароль. Права у всех одинаковые («Исполнитель»).</p>
            <div className="flex gap-2">
              <input className="input flex-1 text-xs" readOnly value={sharedLink} onFocus={(e) => e.currentTarget.select()} />
              <button className="btn-soft" onClick={() => { void navigator.clipboard?.writeText(sharedLink); toast({ kind: 'success', text: 'Скопировано' }); }}><Copy className="w-4 h-4" /> Копировать</button>
            </div>
            <p className="text-[11px] text-ink-3">Ссылка постоянная — можно раздавать многократно.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TabBtn({ label, icon: Icon, active, count, onClick }: {
  label: string; icon: typeof Users; active: boolean; count: number; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn('flex items-center gap-2 px-3 h-7 rounded-full text-sm font-medium transition-all shrink-0 whitespace-nowrap',
        active ? 'bg-accent text-white shadow-soft' : 'text-ink-2 hover:bg-surface-3')}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      <span className={cn('text-[10px] px-1.5 h-4 rounded-full grid place-items-center font-bold tabular-nums',
        active ? 'bg-white/25 text-white' : 'bg-ink-3/15 text-ink-2')}>{count}</span>
    </button>
  );
}

/** Цвет уровня сотрудника — один и тот же код для ведущего и диджея. */
const LEVEL_COLOR: Record<1 | 2 | 3, string> = { 1: '#2563eb', 2: '#d97706', 3: '#16a34a' };

function EmployeeCard({ name, role, phone, email, levelHost, levelDj, avatar, dealsCount, onClick }: {
  name: string; role?: string; phone?: string; email?: string;
  levelHost?: 1 | 2 | 3 | null; levelDj?: 1 | 2 | 3 | null;
  /** Фото из «Профиля» сотрудника (users.avatar) — если не загружено, кружок с инициалами. */
  avatar?: string | null;
  dealsCount: number; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="panel p-3 flex items-center gap-3 text-left hover:border-accent transition-all group"
    >
      {avatar ? (
        <img src={avatar} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-11 h-11 rounded-full grid place-items-center text-white font-bold text-sm flex-shrink-0"
          style={{ backgroundColor: `hsl(${hashHue(name)}, 55%, 48%)` }}>
          {initialsOf(name)}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-ink-1 truncate">{name}</div>
        <div className="text-[11px] text-ink-3 flex items-center gap-2">
          {role && <span className="chip !h-4 !text-[9px]">{role}</span>}
          {levelHost && (
            <span className="chip !h-6 !text-[13px] !px-2.5 font-bold" style={{ backgroundColor: LEVEL_COLOR[levelHost], color: '#fff' }} title="Уровень как ведущего">🎤 Уровень {levelHost}</span>
          )}
          {levelDj && (
            <span className="chip !h-6 !text-[13px] !px-2.5 font-bold" style={{ backgroundColor: LEVEL_COLOR[levelDj], color: '#fff' }} title="Уровень как диджея">🎧 Уровень {levelDj}</span>
          )}
          {dealsCount > 0 && <span className="text-accent font-semibold">{dealsCount} сделок</span>}
        </div>
        {(phone || email) && (
          <div className="text-[10px] text-ink-3 truncate mt-0.5 flex items-center gap-2">
            {phone && <span className="flex items-center gap-0.5"><Phone className="w-2.5 h-2.5" />{phone}</span>}
            {email && <span className="flex items-center gap-0.5 truncate"><Mail className="w-2.5 h-2.5" />{email}</span>}
          </div>
        )}
      </div>
      <Pencil className="w-3.5 h-3.5 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
    </button>
  );
}

function EmptyEmployees({ onShareLink }: { onShareLink?: () => void }) {
  return (
    <div className="panel p-8 text-center max-w-md mx-auto">
      <Users className="w-10 h-10 text-ink-3 mx-auto mb-2" />
      <p className="text-sm text-ink-2 mb-3">Сотрудников пока нет. Раздайте общую ссылку — каждый зарегистрируется сам.</p>
      {onShareLink && (
        <button className="btn-primary" onClick={onShareLink}>
          <Link2 className="w-4 h-4" /> Общая ссылка для регистрации
        </button>
      )}
    </div>
  );
}
